import React, { useEffect, useState } from 'react'
import styled from 'styled-components'
import { getSignedAttachmentUrl } from '../../utils/openSignedAttachment'
import { getUserInitials } from '../../utils/userInitials'

interface UserAvatarProps {
  src?: string | null
  name?: string | null
  size?: number
  fontSize?: string
  className?: string
  style?: React.CSSProperties
  title?: string
  onClick?: () => void
}

const AvatarContainer = styled.div<{ $size: number }>`
  width: ${({ $size }) => $size}px;
  height: ${({ $size }) => $size}px;
  min-width: ${({ $size }) => $size}px;
  min-height: ${({ $size }) => $size}px;
  border-radius: 50%;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  background-color: #0090D3;
  user-select: none;
`

const AvatarImage = styled.img`
  width: 100%;
  height: 100%;
  border-radius: 50%;
  object-fit: cover;
  display: block;
`

const AvatarFallbackText = styled.span<{ $fontSize: string }>`
  color: #FFFFFF;
  font-weight: 600;
  font-size: ${({ $fontSize }) => $fontSize};
  line-height: 1;
  text-transform: uppercase;
  display: flex;
  align-items: center;
  justify-content: center;
`

const isExternalWebUrl = (url: string): boolean => {
  if (!url) return false
  if (
    url.includes('storage.googleapis.com') ||
    url.includes('storage.cloud.google.com') ||
    url.startsWith('gs://')
  ) {
    return false
  }
  return url.startsWith('http://') || url.startsWith('https://')
}

export const UserAvatar: React.FC<UserAvatarProps> = ({
  src,
  name,
  size = 32,
  fontSize,
  className,
  style,
  title,
  onClick,
}) => {
  const [resolvedUrl, setResolvedUrl] = useState<string>('')
  const [hasError, setHasError] = useState<boolean>(false)

  const computedFontSize = fontSize || `${Math.max(10, Math.floor(size * 0.42))}px`

  useEffect(() => {
    let isMounted = true
    setHasError(false)

    if (!src || typeof src !== 'string' || !src.trim()) {
      setResolvedUrl('')
      return
    }

    let normalized = src.trim()
    if (normalized.includes('stock_uploads')) {
      normalized = normalized.replace('stock_uploads', 'ordeno-general-attachments')
    }

    if (
      normalized.startsWith('data:') ||
      normalized.startsWith('blob:') ||
      normalized.includes('googleusercontent.com') ||
      normalized.includes('X-Goog-Signature') ||
      isExternalWebUrl(normalized)
    ) {
      setResolvedUrl(normalized)
      return
    }

    getSignedAttachmentUrl(normalized)
      .then((signedUrl) => {
        if (isMounted) {
          setResolvedUrl(signedUrl || normalized)
        }
      })
      .catch(() => {
        if (isMounted) {
          setResolvedUrl(normalized)
        }
      })

    return () => {
      isMounted = false
    }
  }, [src])

  const effectiveTitle = title ?? name ?? undefined

  if (resolvedUrl && !hasError) {
    return (
      <AvatarContainer $size={size} className={className} style={style} title={effectiveTitle} onClick={onClick}>
        <AvatarImage
          src={resolvedUrl}
          alt={name || 'Avatar'}
          draggable={false}
          onError={() => setHasError(true)}
        />
      </AvatarContainer>
    )
  }

  return (
    <AvatarContainer $size={size} className={className} style={style} title={effectiveTitle} onClick={onClick}>
      <AvatarFallbackText $fontSize={computedFontSize}>
        {getUserInitials(name)}
      </AvatarFallbackText>
    </AvatarContainer>
  )
}

export default UserAvatar
